// src/components/DeleteJobActionButton.js


//ORIGINGL
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  FormHelperText,
} from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import alertIcon from '../../../../src/img/warning-icon.svg'; // Ensure the path is correct
import Notification from '../../../components/Notification';
import { useDeleteJobMutation } from '../../../services/jobsApi';

// Styles for the modal to center it and apply consistent styling
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  border: 'none',
  borderRadius: '1.5em',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const DeleteJobActionButton = ({
  buttonText = 'Cancel Job',
  buttonVariant = 'contained',
  buttonColor = 'secondary',
  buttonStartIcon = null,
  confirmationTitle = 'Are you sure you want to cancel this job?',
  confirmationDescription = 'This action cannot be undone.',
  jobId, // ID of the job to cancel
  successMessage = 'Job canceled successfully, you will be redirected to the dashboard.',
  errorMessage = 'There was an error canceling the job.',
  redirectUrl = '/dashboard/admintools', // URL to redirect after canceling the job
  disabled = false,
  ...buttonProps
}) => {
  // State to control the visibility of the confirmation modal
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // State to control the visibility of the information modal after deletion
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  // State to hold the message displayed in the information modal
  const [infoMessage, setInfoMessage] = useState('');
  // State to manage notifications (e.g., error messages)
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // States for the deletion reason and additional details
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteDetails, setDeleteDetails] = useState('');
  // State to handle validation errors
  const [error, setError] = useState('');

  // Mutation hook to perform the Cancel operation
  const [deleteJob, { isLoading: loading }] = useDeleteJobMutation();

  const reasonToValidate = 'Other';

  // Function to handle the deletion process
  const handleDelete = async () => {
    // Validation: Ensure a reason has been selected
    if (!deleteReason) {
      setError('Please select a reason for canceling the job.');
      return;
    }

    if (deleteReason === reasonToValidate && !deleteDetails.trim()) {
      setError('Details are required.');
      return;
    }
    setError('');

    try {
      // Create the cancel payload with reason and details
      const deletePayload = {
        jobId,
        deleteReason,
        deleteDetails,
      };

      // Perform the cancel operation
      await deleteJob(deletePayload).unwrap();
      // Set the success message and open the information modal
      setInfoMessage(successMessage);
      setIsInfoOpen(true);
      // Redirect the user after a short delay
      if (redirectUrl) {
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 2000);
      }
    } catch (error) {
      // Log the error and show a notification
      console.error(error);
      setNotification({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      // Close the confirmation modal and reset fields
      setIsConfirmOpen(false);
      setDeleteReason('');
      setDeleteDetails('');
      setError('');
    }
  };

  // Function to handle closing the information modal
  const handleCloseInfo = () => {
    setIsInfoOpen(false);
    // Redirect the user if a redirect URL is provided
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  // Options for the deletion reasons dropdown
  const deletionReasons = [
    'No Tickets Available',
    'Pricing Issues',
    'Multi Buy Fail',
    'Delivery issue',
    'Event not on sale',
    'Event canceled/postponed',
    'Drone Buy needed',
    'Job not needed anymore',
    'Other',
  ];

  return (
    <>
      {/* Main cancel Button */}
      <Button
        variant={buttonVariant}
        color={buttonColor}
        startIcon={buttonStartIcon}
        onClick={() => setIsConfirmOpen(true)}
        disabled={disabled}
        style={
          disabled
            ? {
              whiteSpace: 'nowrap',
              backgroundColor: 'white',
              color: 'var(--gray)',
              border: '1px solid var(--gray)',
            }
            : {
              whiteSpace: 'nowrap',
              backgroundColor: 'white',
              color: 'var(--red)',
              border: '1px solid var(--red)',
            }
        }
        {...buttonProps}
      >
        {buttonText}
      </Button>

      {/* Custom Confirmation Modal */}
      <Modal
        open={isConfirmOpen}
        onClose={(event, reason) => {
          // Prevent closing the modal while the cancel operation is in progress
          if (loading) return;
          // Close the modal and reset fields if not loading
          setIsConfirmOpen(false);
          setError('');
          setDeleteReason('');
          setDeleteDetails('');
        }}
        aria-labelledby="delete-job-modal-title"
        aria-describedby="delete-job-modal-description"
      >
        <Box sx={modalStyle}>
          {/* Alert Icon */}
          <Box display="flex" justifyContent="center">
            <img src={alertIcon} alt="alert icon" height="32px" />
          </Box>
          {/* Modal Title */}
          <Typography id="delete-job-modal-title" variant="h6" component="h2" align="center" sx={{ mt: 2 }}>
            {confirmationTitle}
          </Typography>

          {/* Modal Description */}
          <Typography id="delete-job-modal-description" sx={{ mt: 2 }}>
            {confirmationDescription}
          </Typography>

          {/* Dropdown to select the reason */}
          <FormControl fullWidth error={!!error} margin="normal" disabled={loading}>
            <label id="delete-reason-label" >Reason for Cancelation *</label>
            <Select
            sx={{ margin:'0 0 0.5em 0' }}
              labelId="delete-reason-label"
              id="delete-reason"
              value={deleteReason}
              onChange={(e) => {
                setDeleteReason(e.target.value);
                setError("");
              }}
            >
              {deletionReasons.map((reason, index) => (
                <MenuItem value={reason} key={index}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
            {!deleteReason && error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>

          {/* Text area for additional details */}
          <label>Additional Details {deleteReason === reasonToValidate ? '(Required)' : '(Optional)'}</label>
          <TextField
            sx={{ margin: '0' }}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            margin="normal"
            value={deleteDetails}
            onChange={(e) => setDeleteDetails(e.target.value)}
            disabled={loading}
            error={deleteReason === reasonToValidate && !deleteDetails.trim() && error}
            helperText={ deleteReason === reasonToValidate && !deleteDetails.trim() && error ? error  : '' }
          />

          {/* Modal Action Buttons */}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            {/* Cancel Button */}
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                // Prevent closing if loading
                if (loading) return;
                setIsConfirmOpen(false);
                setError('');
                setDeleteReason('');
                setDeleteDetails('');
              }}
              disabled={loading} // Disable the button while loading
              style={{ marginRight: '1em', width: '100px' }}
            >
              Cancel
            </Button>
            {/* Cancel Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleDelete}
              disabled={loading} // Disable the button while loading
              style={{ width: '110px' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Cancel Job'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Information Modal (Success Message) */}
      <Modal
        open={isInfoOpen}
        onClose={handleCloseInfo}
        aria-labelledby="info-modal-title"
        aria-describedby="info-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="info-modal-title" variant="h6" component="h2" align="center">
            {infoMessage}
          </Typography>
        </Box>
      </Modal>

      {/* Notification for Errors */}
      <Notification
        status={notification.severity.toUpperCase()}
        text={notification.message}
        snackBarOpenProp={notification.open}
        handleClose={() => setNotification({ ...notification, open: false })}
      />
    </>
  );
};

// PropTypes to ensure correct prop types are passed to the component
DeleteJobActionButton.propTypes = {
  buttonText: PropTypes.string,
  buttonVariant: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonStartIcon: PropTypes.element,
  confirmationTitle: PropTypes.string,
  confirmationDescription: PropTypes.string,
  jobId: PropTypes.number.isRequired,
  successMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  redirectUrl: PropTypes.string,
  disabled: PropTypes.bool,
};

export default DeleteJobActionButton;
